import { curry, map, reduce, filter, tail, isNil, reject, length } from 'ramda';
import intersect from 'fast_array_intersect';
import filterMethods from './filter';
import { FilterList, FilterOptions, HydratedFilter, FilterListLogic } from './types';

/**
 * Filters a list of items by the supplied filter.
 *
 * @param {[Object]} items list of items
 * @param {Object} filter filterobject
 * @returns {[Object]|null} filtered items
 */
const applyFilter = curry((items: Array<any>, filterObj: HydratedFilter) => {
  const value = filterObj.value.value;

  const res =
    filterObj.value.mode && filterObj.value.value
      ? filter(
          filterMethods[filterObj.type][filterObj.value.mode]({
            ...filterObj,
            value: {
              ...filterObj.value,
              value,
            },
          }),
          items
        )
      : null;

  if (filterObj.processFilterResult) {
    return filterObj.processFilterResult(res);
  }

  return res;
});

const allReducer: (acc: Array<any>, current: Array<any>) => Array<any> = (result, current) =>
  intersect([result, current]);

const anyReducer: (acc: Array<any>, current: Array<any>) => Array<any> = (result, current) => [
  ...new Set([...result, ...current]),
];

const getReducer = (mode: FilterListLogic) => {
  if (mode === 'all') {
    return allReducer;
  } else if (mode === 'any') {
    return anyReducer;
  }

  throw new Error(`Unsupported mode ${mode}`);
};

/**
 * Filters a list of items by a list of filters, and then applies either
 * intersection (if _all_ filters must be applied) or union (if _any_ filters can apply)
 *
 * @param {Object} filterOptions filter options
 * @param {Object} filters filter object with mode and list of filters
 * @param {[Object]} items list of items
 * @returns {[Object]} filtered items
 */
const applyFilters = curry((filterOptions: FilterOptions, { mode, filters }: FilterList, items: Array<any>) => {
  if (length(filters) === 0) return items;

  const hydratedFilters = map(
    (filterObj) => ({
      ...filterOptions[filterObj.name],
      ...filterObj,
    }),
    filters
  );

  const sets: Array<Array<any>> = reject(isNil, map(applyFilter(items), hydratedFilters));

  if (sets.length === 0) return items;
  if (sets.length === 1) return sets[0];

  const reducer: (acc: Array<any>, current: Array<any>) => Array<any> = getReducer(mode);

  return reduce(reducer, sets[0], tail(sets));
});

export default applyFilters;
